import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, del, get, post, postForm, put, withQuery } from "./client";

/** `/api/v1/admin/*`: administration. Every write hands the changed list
 * back, so the screen never shows a stale row after a save. */

const BASE = "/api/v1/admin";

// --- the side nav's counts ---------------------------------------------------------

/** How many of each thing a section holds, for the side nav. One request of
 * COUNTs, so the nav never pulls every section's whole list to number it. */
export interface AdminCounts {
  providers: number;
  users: number;
  spares: number;
  price_lists: number;
  flange_tables: number;
  meeting_points: number;
  audit: number;
}

export const COUNTS_KEY = ["admin", "counts"] as const;

export function useAdminCounts() {
  return useQuery<AdminCounts, ApiError>({ queryKey: COUNTS_KEY, queryFn: () => get(`${BASE}/counts`) });
}

// --- providers -------------------------------------------------------------------

export type QuoteTemplate = "seapro" | "macsteel";

export const QUOTE_TEMPLATES: { value: QuoteTemplate; label: string }[] = [
  { value: "seapro", label: "Seapro (landscape grid, discount at the foot)" },
  { value: "macsteel", label: "Macsteel (quote confirmation, Disc% per line)" },
];

export interface Provider {
  id: number;
  acc_no: string;
  name: string;
  contact_person: string | null;
  contact_number: string | null;
  email: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  address_line_3: string | null;
  address_line_4: string | null;
  vatable: boolean;
  active: boolean;
  registration_no: string | null;
  vat_no: string | null;
  agent_line: string | null;
  /** Which of the supplier's own templates its quotation is drawn on. */
  quote_template: QuoteTemplate;
  /** What Macsteel's letterhead prints beyond Seapro's: the registered
   * company, the branch fax, and our account number with them. */
  legal_name: string | null;
  fax_number: string | null;
  customer_account_no: string | null;
  labour_rate: string | null;
  labour_description: string | null;
  km_rate: string | null;
  discount_pct: string | null;
  logo_url: string | null;
  agent_mark_url: string | null;
  login: { id: number; email: string; active: boolean } | null;
  /** Active logins of any role linked to this provider. */
  logins: number;
  /** What it is to us, read off what hangs from it: "crew", "supplier",
   * both, or neither yet. A description for the list, never a scope. */
  kinds: ("crew" | "supplier")[];
}

export interface Providers {
  rows: Provider[];
}

export type ProviderIn = Partial<Omit<Provider, "id" | "acc_no" | "logo_url" | "agent_mark_url" | "login" | "discount_pct" | "logins" | "kinds">> & { name: string };

export const PROVIDERS_KEY = ["admin", "providers"] as const;

export function useProviders() {
  return useQuery<Providers, ApiError>({ queryKey: PROVIDERS_KEY, queryFn: () => get(`${BASE}/providers`) });
}

function useListWrite<TData, TVars>(key: readonly unknown[], write: (vars: TVars) => Promise<TData>, also: readonly unknown[][] = []) {
  const client = useQueryClient();
  return useMutation<TData, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (data) => {
      client.setQueryData(key, data);
      void client.invalidateQueries({ queryKey: COUNTS_KEY });
      for (const other of also) {
        void client.invalidateQueries({ queryKey: other });
      }
    },
  });
}

export function useCreateProvider() {
  return useListWrite(PROVIDERS_KEY, (body: ProviderIn & { acc_no: string; login_email?: string | null; login_password?: string | null }) =>
    post<Providers>(`${BASE}/providers`, body),
  );
}

export function useUpdateProvider() {
  return useListWrite(PROVIDERS_KEY, (vars: { id: number; body: ProviderIn }) => put<Providers>(`${BASE}/providers/${vars.id}`, vars.body));
}

export function useCreateProviderLogin() {
  return useListWrite(PROVIDERS_KEY, (vars: { id: number; login_email: string; login_password: string }) =>
    post<Providers>(`${BASE}/providers/${vars.id}/login`, { login_email: vars.login_email, login_password: vars.login_password }),
  );
}

export function useUploadArtwork() {
  return useListWrite(PROVIDERS_KEY, (vars: { id: number; kind: "logo" | "agent_mark"; file: File }) => {
    const form = new FormData();
    form.append("kind", vars.kind);
    form.append("artwork", vars.file, vars.file.name);
    return postForm<Providers>(`${BASE}/providers/${vars.id}/artwork`, form);
  });
}

export function useDeleteProvider() {
  return useListWrite(PROVIDERS_KEY, (id: number) => del<Providers>(`${BASE}/providers/${id}`));
}

// --- users -------------------------------------------------------------------------

export interface UserRow {
  id: number;
  email: string;
  role: string;
  provider_id: number | null;
  provider: string | null;
  active: boolean;
  last_login_at: string | null;
  is_me: boolean;
}

export interface Users {
  rows: UserRow[];
  providers: { id: number; name: string; active: boolean }[];
  roles: { value: string; label: string }[];
  screens: { key: string; label: string; href: string; admin_gated: boolean }[];
  permitted: Record<string, string[]>;
  visible: Record<string, Record<string, boolean>>;
  notice: string | null;
  /** Whether the reader is an administrator, who alone may change logins,
   * roles and this matrix; the server refuses anyone else with a 403. */
  may_administer: boolean;
}

export const USERS_KEY = ["admin", "users"] as const;

export function useUsers() {
  return useQuery<Users, ApiError>({ queryKey: USERS_KEY, queryFn: () => get(`${BASE}/users`) });
}

export function useCreateUser() {
  return useListWrite(USERS_KEY, (body: { email: string; role: string; provider_id: number | null; password: string | null }) => post<Users>(`${BASE}/users`, body));
}

export function useToggleUser() {
  return useListWrite(USERS_KEY, (id: number) => post<Users>(`${BASE}/users/${id}/active`));
}

export function useResetPassword() {
  return useListWrite(USERS_KEY, (vars: { id: number; password: string | null }) => post<Users>(`${BASE}/users/${vars.id}/password`, { password: vars.password }));
}

export function useChangeRole() {
  return useListWrite(USERS_KEY, (vars: { id: number; role: string; provider_id: number | null }) =>
    post<Users>(`${BASE}/users/${vars.id}/role`, { role: vars.role, provider_id: vars.provider_id }),
  );
}

export function useSaveVisibility() {
  return useListWrite(USERS_KEY, (visible: Record<string, string[]>) => put<Users>(`${BASE}/users/screens`, { visible }), [["me"]]);
}

/** The matrix as the server wants it back: every ticked pair, per role. */
export function shownKeys(matrix: Record<string, Record<string, boolean>>): Record<string, string[]> {
  return Object.fromEntries(
    Object.entries(matrix).map(([role, screens]) => [role, Object.entries(screens).filter(([, on]) => on).map(([key]) => key)]),
  );
}

// --- spares ------------------------------------------------------------------------

export interface SpareRow {
  id: number;
  supplier: string;
  size: string;
  part_description: string;
  supplier_item_code: string | null;
  unit_cost: string | null;
  selling_price: string | null;
  markup: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  needs_code: boolean;
  priced_from_list: boolean;
  source: string | null;
}

export interface Spares {
  rows: SpareRow[];
  suppliers: { id: number; name: string; acc_no: string }[];
  codes_by_supplier: Record<string, { code: string; label: string; cost: string | null; selling: string | null }[]>;
  lists_unread: string[];
  selected_supplier: number | null;
  no_supplier: string;
  counts: { needing_code: number; unpriced: number; priced_from_list: number };
}

export const sparesKey = (supplierId: number | null) => ["admin", "spares", supplierId] as const;

export function useSpares(supplierId: number | null) {
  return useQuery<Spares, ApiError>({
    queryKey: sparesKey(supplierId),
    queryFn: () => get(withQuery(`${BASE}/spares`, { supplier_id: supplierId })),
  });
}

export function useUpdateSpare() {
  const client = useQueryClient();
  return useMutation<Spares, ApiError, { id: number; body: { supplier_item_code: string | null; unit_cost: string | null; selling_price: string | null; supplier_id: string | null } }>({
    mutationFn: (vars) => put<Spares>(`${BASE}/spares/${vars.id}`, vars.body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "spares"] });
    },
  });
}

// --- meeting points --------------------------------------------------------------------

export interface MeetingPoints {
  contract: { id: number; name: string } | null;
  rows: { id: number; name: string; address: string | null; lat: number; lon: number; region_id: number | null; region: string | null; active: boolean; created_at: string | null }[];
  regions: { id: number; name: string }[];
}

export const MEETING_POINTS_KEY = ["admin", "meeting-points"] as const;

export function useMeetingPoints() {
  return useQuery<MeetingPoints, ApiError>({ queryKey: MEETING_POINTS_KEY, queryFn: () => get(`${BASE}/meeting-points`) });
}

export function useCreateMeetingPoint() {
  return useListWrite(MEETING_POINTS_KEY, (body: { name: string; lat: number | null; lon: number | null; address: string | null; region_id: number | null }) =>
    post<MeetingPoints>(`${BASE}/meeting-points`, body),
  );
}

export function useSetMeetingPointActive() {
  return useListWrite(MEETING_POINTS_KEY, (vars: { id: number; active: boolean }) => post<MeetingPoints>(`${BASE}/meeting-points/${vars.id}/active`, { active: vars.active }));
}

// --- settings ---------------------------------------------------------------------------

export interface Setting {
  name: string;
  label: string;
  why: string;
  kind: "fraction" | "int" | "text" | "decimal" | "flag";
  default: string;
  value: string;
  history: { value: string; effective_from: string | null; created_at: string | null; by: string | null; reason: string | null }[];
}

export interface Settings {
  rows: Setting[];
  saved: string | null;
}

export const SETTINGS_KEY = ["admin", "settings"] as const;

export function useSettings() {
  return useQuery<Settings, ApiError>({ queryKey: SETTINGS_KEY, queryFn: () => get(`${BASE}/settings`) });
}

/** A setting can be the scheduling gate, which the planner's count, every
 * readiness panel and the override list read: all are marked stale (cheap,
 * so on every save rather than only that key's). */
const GATE_READERS: readonly unknown[][] = [["routing", "count"], ["readiness"], ["overrides"]];

export function useSaveSetting() {
  return useListWrite(
    SETTINGS_KEY,
    (body: { key: string; value: string; reason: string }) => put<Settings>(`${BASE}/settings`, body),
    GATE_READERS,
  );
}

// --- flange tables ----------------------------------------------------------------------

export interface FlangeTables {
  versions: { source_version: string; rows: number; active: boolean; imported_at: string | null }[];
  active: { source_version: string; rows: number } | null;
  sheet_name: string;
  configured_version: string;
  configured_workbook: string;
  max_upload_mb: number;
  loaded: string | null;
}

export const FLANGE_KEY = ["admin", "flange-tables"] as const;

export function useFlangeTables() {
  return useQuery<FlangeTables, ApiError>({ queryKey: FLANGE_KEY, queryFn: () => get(`${BASE}/flange-tables`) });
}

export function useUploadFlangeTables() {
  return useListWrite(FLANGE_KEY, (vars: { version: string; file: File }) => {
    const form = new FormData();
    form.append("source_version", vars.version);
    form.append("file", vars.file, vars.file.name);
    return postForm<FlangeTables>(`${BASE}/flange-tables`, form);
  });
}

export function useReloadFlangeTables() {
  return useListWrite(FLANGE_KEY, () => post<FlangeTables>(`${BASE}/flange-tables/reload`));
}

// --- price lists -------------------------------------------------------------------------

export interface PriceListVersion {
  id: number;
  supplier_id: number;
  version_no: number;
  filename: string;
  sheet: string;
  status: "draft" | "active" | "superseded";
  discount_pct: string | null;
  rows_loaded: number;
  rows_skipped: number;
  notes: string | null;
  uploaded_at: string | null;
  activated_at: string | null;
}

/** A service kit's size, as the price-list index names the kits in force. */
export interface PriceListKitSize {
  size_label: string;
  dn_mm: number;
  series: string | null;
}

/** One standard line of a service kit, priced from that version's own parts. */
export interface PriceListKitLine {
  seq: number;
  item_code: string;
  description: string | null;
  qty: string;
  list_price: string | null;
  net: string | null;
  labour: boolean;
  in_version: boolean;
}

/** A service kit as the price-list screen shows it: read-only (decision 8).
 * `k1` is what a pre-issued BOQ is priced at for it, before travelling and the
 * pilot; null when a code is missing from the version. */
export interface PriceListKit extends PriceListKitSize {
  lines: number;
  kit_id: number;
  in_force: boolean;
  standard: PriceListKitLine[];
  pilot_options: { model: string; item_code: string; description: string | null; list_price: string | null }[];
  net_total: string;
  k1: string | null;
  complete: boolean;
}

export interface PriceLists {
  rows: {
    supplier: { id: number; name: string; acc_no: string };
    versions: PriceListVersion[];
    active_id: number | null;
    parts_in_force: number;
    kits_in_force: PriceListKitSize[];
  }[];
  suppliers: { id: number; name: string }[];
  sheets: string[];
  max_upload_mb: number;
}

export interface PriceListDetail {
  price_list: PriceListVersion;
  supplier: { id: number; name: string } | null;
  in_force_id: number | null;
  can_activate: boolean;
  items: { id: number; supplier_item_code: string | null; sap_code: string | null; size: string; part_description: string; unit_cost: string | null; selling_price: string | null }[];
  kits: PriceListKit[];
  events: { action: string; at: string | null; by: string | null; detail: string | null }[];
}

export const PRICE_LISTS_KEY = ["admin", "price-lists"] as const;
export const priceListKey = (id: number) => ["admin", "price-lists", id] as const;

export function usePriceLists() {
  return useQuery<PriceLists, ApiError>({ queryKey: PRICE_LISTS_KEY, queryFn: () => get(`${BASE}/price-lists`) });
}

export function usePriceList(id: number | null) {
  return useQuery<PriceListDetail, ApiError>({
    queryKey: priceListKey(id ?? 0),
    queryFn: () => get(`${BASE}/price-lists/${id}`),
    enabled: id !== null,
  });
}

export function useUploadPriceList() {
  const client = useQueryClient();
  return useMutation<PriceListDetail, ApiError, { supplierId: number; sheet: string; notes: string; file: File }>({
    mutationFn: (vars) => {
      const form = new FormData();
      form.append("supplier_id", String(vars.supplierId));
      form.append("sheet", vars.sheet);
      if (vars.notes.trim() !== "") {
        form.append("notes", vars.notes.trim());
      }
      form.append("file", vars.file, vars.file.name);
      return postForm<PriceListDetail>(`${BASE}/price-lists`, form);
    },
    onSuccess: (detail) => {
      client.setQueryData(priceListKey(detail.price_list.id), detail);
      void client.invalidateQueries({ queryKey: PRICE_LISTS_KEY });
    },
  });
}

export function useActivatePriceList(id: number) {
  const client = useQueryClient();
  return useMutation<PriceListDetail, ApiError, void>({
    mutationFn: () => post<PriceListDetail>(`${BASE}/price-lists/${id}/activate`),
    onSuccess: (detail) => {
      client.setQueryData(priceListKey(id), detail);
      void client.invalidateQueries({ queryKey: PRICE_LISTS_KEY });
      void client.invalidateQueries({ queryKey: ["admin", "spares"] });
    },
  });
}

// --- audit -------------------------------------------------------------------------------

export interface AuditFilter {
  job: string;
  actor: string;
  table: string;
  since_days: string;
  offset: number;
}

export interface Audit {
  total: number;
  offset: number;
  limit: number;
  entries: { id: number; table: string; row_id: string; action: string; at: string | null; actor_id: number | null; actor: string | null; reason: string | null; job_site_id: number | null; changes: Record<string, unknown> | null }[];
  tables: string[];
  actors: { id: number; email: string }[];
  filters: { job: number | null; actor: number | null; table: string | null; since_days: number | null };
  site: { id: number; name: string } | null;
}

export function useAudit(filter: AuditFilter) {
  return useQuery<Audit, ApiError>({
    queryKey: ["admin", "audit", filter],
    queryFn: () => get(withQuery(`${BASE}/audit`, { ...filter, offset: filter.offset || null })),
  });
}

// --- backload -----------------------------------------------------------------------------

export interface BackloadReport {
  filename: string;
  quotes_seen: number;
  lines_seen: number;
  errors: string[];
  results: { quote_ref: string; outcome: string; reason: string | null; quotation_id: number | null; site_id: number | null; site_name: string | null; candidates: { id: number; name: string }[] }[];
}

export function useBackload() {
  const client = useQueryClient();
  return useMutation<BackloadReport, ApiError, File>({
    mutationFn: (file) => {
      const form = new FormData();
      form.append("workbook", file, file.name);
      return postForm<BackloadReport>(`${BASE}/quotations/backload`, form);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["quotations"] });
    },
  });
}

// --- help ----------------------------------------------------------------------------------

export interface Help {
  walkthrough_url: string;
  handbook_available: boolean;
  handbook_url: string;
  /** The field operators' own handbook: the phone screens and the checklist. */
  field_handbook_available: boolean;
  field_handbook_url: string;
}

export function useHelp() {
  return useQuery<Help, ApiError>({ queryKey: ["help"], queryFn: () => get("/api/v1/help") });
}
